import { useCallback, useMemo, useState } from 'react';

import {
  ZfBanner,
  ZfBox,
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfDivider,
  ZfFormField,
  ZfIcon,
  ZfIconButton,
  ZfInput,
  ZfSelectOption,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';
import { Formik } from 'formik';
import { addCashAccounts } from 'services/portfolios/portfolioService';
import { useGetDropdown } from './hooks/useGetDropdown';
import { List } from 'react-window';
import { RowProps } from './PortfolioDetailSection';
import { PortfolioTableItemDetails } from '../../views/PortfolioSummary/PortfolioTableItem';
import AGGridComponent from '../AgGrid/AGGridComponent';
import { ColDef } from 'ag-grid-enterprise';
import { closeModal } from '../../reducers/modalReducer';
import useGetPortfoliosList from '../../views/PortfolioSummary/hooks/useGetPortfoliosList';
import { CashAccountSource, PortfolioCashAccountDetails, ResponseError } from 'pco-product-service-internal-consumer';
import { useDispatch } from 'react-redux';
import { BannerErrorDialog } from './BannerErrorDialog';
import { searchSuggestionFilterData } from '../../utils/searchSuggestionFilterUtils';
import { useTranslation } from 'react-i18next';
import CashAccountSchema from './cashAccountSchema';

type AddCashAccountDialogType = {
  onProceed: () => void;
  portfolio: PortfolioTableItemDetails;
  brUid: string;
};

const DROPDOWN_HEIGHT = 32;
const AddCashAccountDialog = ({ onProceed, portfolio, brUid }: AddCashAccountDialogType) => {
  const initialValues = useMemo(() => {
    return {
      cashAccounts: [{ currency: '', accountType: '' }],
    };
  }, []);

  const { t } = useTranslation();

  const { data } = useGetPortfoliosList(brUid || '', CashAccountSource.Pco);
  const dispatch = useDispatch();

  const portfolioData = useMemo(() => {
    if (data && data.list) {
      return data.list.find((item) => item.portfolioId === portfolio.portfolioId);
    }
    return undefined;
  }, [data, portfolio]);

  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorObject, setErrorObject] = useState<Error | undefined>(undefined);

  const { currencyData, cashAccountTypeData } = useGetDropdown();

  const [cashAccountSuggestion, setCashAccountSuggestion] = useState('');
  const [currencySuggestion, setCurrencySuggestion] = useState('');

  const cashAccountFilteredData = searchSuggestionFilterData({
    data: cashAccountTypeData,
    searchTerm: cashAccountSuggestion,
  });
  const currencyFilteredData = searchSuggestionFilterData({ data: currencyData, searchTerm: currencySuggestion });

  const Row = useCallback(
    ({ index, style, item }: RowProps) => {
      const co = cashAccountFilteredData?.[index];
      if (!co) return null;

      return (
        <ZfSelectOption style={style} value={co.value.code} isSelected={item.accountType === co.value.value}>
          {co.value.label}
        </ZfSelectOption>
      );
    },
    [cashAccountFilteredData],
  );

  const handleClose = () => {
    dispatch(closeModal());
  };

  const getColumnDefs = useMemo((): ColDef<PortfolioCashAccountDetails>[] => {
    return [
      {
        headerName: t('pb.cashaccount.grid.header.requesteddate'),
        field: 'createdAt',
        flex: 1,
        filter: false,
      },
      {
        headerName: t('pb.cashaccount.grid.header.type'),
        field: 'accountType',
        valueFormatter: (params) => cashAccountTypeData?.find((item) => item.key === params.value)?.value?.label || '',
        flex: 1,
        filter: false,
      },

      { headerName: t('pb.cashaccount.grid.header.ccy'), field: 'currency', flex: 1, filter: false },
      {
        headerName: t('pb.cashaccount.grid.header.status'),
        field: 'accountStatus',
        valueFormatter: () => t('pb.cashaccount.grid.status.requested'),
        flex: 1,
        filter: false,
      },
    ];
  }, [cashAccountTypeData, t]);

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange
      enableReinitialize={true}
      validationSchema={CashAccountSchema}
      initialValues={initialValues}
      onSubmit={async (data, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        setShowErrorBanner(false);
        setErrorObject(undefined);
        try {
          await addCashAccounts(portfolio.portfolioId, data.cashAccounts);
          resetForm();
          setSubmitting(false);
          onProceed();
          handleClose();
        } catch (e) {
          if (e instanceof ResponseError) {
            try {
              const errorBody = await e.response.json();
              setErrorObject(errorBody);
            } catch {
              setErrorObject(e);
            }
          } else if (e instanceof Error) {
            setErrorObject(e);
          } else {
            setErrorObject(new Error(String(e)));
          }
          setShowErrorBanner(true);
          setSubmitting(false);
        }
      }}
    >
      {({ setFieldValue, values, errors, submitForm, touched, isSubmitting }) => {
        return (
          <>
            <ZfDialog show={true} size="medium" onDialogClose={handleClose} disableLightDismiss>
              <ZfDialogHeader>{t('pb.cashaccount.dialog.title')}</ZfDialogHeader>
              <ZfDialogContent>
                <ZfStack spacing="medium">
                  {showErrorBanner && (
                    <ZfBanner
                      type="error"
                      dismissible={false}
                      actionButtonText={t('pb.portfolio.error.details')}
                      onBannerActionButtonClick={() => setShowErrorDialog(true)}
                    >
                      <ZfStack spacing="extra-extra-small">
                        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
                          {t('pb.portfolio.error.title')}
                        </ZfTypography>
                        <ZfTypography variant="label-small" color="emphasis">
                          {t('pb.cashaccount.error.banner.message')}
                        </ZfTypography>
                        <ZfTypography variant="label-small" color="emphasis">
                          {t('pb.portfolio.error.banner.support')}
                        </ZfTypography>
                      </ZfStack>
                    </ZfBanner>
                  )}
                  <ZfStack direction="row" spacing="small" style={{ alignItems: 'center' }}>
                    <ZfTypography>
                      {t('pb.cashaccount.requestedfor', { portfolioNumber: portfolio.portfolioNumber })}
                    </ZfTypography>
                    <ZfDivider style={{ flex: 1 }} />
                  </ZfStack>
                  <AGGridComponent
                    defaultPopupParent
                    id={`cashaccount-${portfolio.portfolioId}`}
                    disableSelection={true}
                    rowKey={'cashAccountId'}
                    getColumnDefs={getColumnDefs}
                    items={(portfolioData?.cashAccounts ?? []) as Record<string, unknown>[]}
                    suppressRowHoverHighlight={false}
                  />{' '}
                  <ZfStack spacing="extra-extra-small">
                    {values.cashAccounts.map((item, index) => {
                      return (
                        <ZfStack
                          direction="row"
                          spacing="small"
                          key={`cashAccount_${index}`}
                          styles={{ alignItems: 'center' }}
                        >
                          <ZfFormField
                            style={{ flex: 1, minHeight: 78 }}
                            label={t('pb.cashaccount.currency.label')}
                            alerts={[
                              typeof errors?.cashAccounts?.[index] === 'object'
                                ? (errors.cashAccounts[index]?.currency ?? '')
                                : '',
                            ]}
                          >
                            <ZfInput
                              name={`cashAccounts-${index}-currency`}
                              type="select"
                              onSelectChange={(e) => {
                                setFieldValue(`cashAccounts.${index}.currency`, e.detail);
                              }}
                              allowSearch
                              error={
                                typeof errors?.cashAccounts?.[index] === 'object' &&
                                !!errors.cashAccounts[index]?.currency &&
                                typeof touched?.cashAccounts?.[index] === 'object' &&
                                !!touched.cashAccounts[index]?.currency
                              }
                              id={`cashAccounts-${index}-currency`}
                              value={item.currency}
                              onInputDropdownOpen={() => setCurrencySuggestion('')}
                              onInputChange={(e) => setCurrencySuggestion(e.detail)}
                              getOptionLabel={(option) =>
                                currencyData?.find((co) => co.value.code === option)?.value?.label || option
                              }
                            >
                              {currencySuggestion !== '' && currencyFilteredData?.length === 0 && (
                                <ZfBox
                                  style={{
                                    padding: 'var(--gap-vertical-xs)',
                                    paddingBottom: 'var(--gap-vertical-xs)',
                                    paddingLeft: 'var(--gap-horizontal-md)',
                                  }}
                                >
                                  <ZfTypography variant="body-small" color="normal">
                                    {t('pb.cashaccount.currency.empty.description')}
                                  </ZfTypography>
                                </ZfBox>
                              )}
                              {currencyFilteredData?.map((co) => {
                                return (
                                  <ZfSelectOption
                                    key={co.key}
                                    value={co.value.value}
                                    isSelected={item.currency === co.value.value}
                                  >
                                    {co.value.label}
                                  </ZfSelectOption>
                                );
                              })}
                            </ZfInput>
                          </ZfFormField>
                          <ZfFormField
                            style={{ flex: 1, minHeight: 78 }}
                            label={t('pb.cashaccount.accounttype.label')}
                            alerts={[
                              typeof errors?.cashAccounts?.[index] === 'object'
                                ? (errors.cashAccounts[index]?.accountType ?? '')
                                : '',
                            ]}
                          >
                            <ZfInput
                              name={`cashAccounts-${index}-accountType`}
                              type="select"
                              onSelectChange={(e) => setFieldValue(`cashAccounts.${index}.accountType`, e.detail)}
                              allowSearch
                              error={
                                typeof errors?.cashAccounts?.[index] === 'object' &&
                                !!errors.cashAccounts[index]?.accountType &&
                                typeof touched?.cashAccounts?.[index] === 'object' &&
                                !!touched.cashAccounts[index]?.accountType
                              }
                              id={`cashAccounts-${index}-accountType`}
                              value={item.accountType}
                              onInputDropdownOpen={() => setCashAccountSuggestion('')}
                              onInputChange={(e) => setCashAccountSuggestion(e.detail)}
                              getOptionLabel={(option) =>
                                cashAccountTypeData?.find((co) => co.value.code === option)?.value?.label || option
                              }
                            >
                              {cashAccountSuggestion !== '' && cashAccountFilteredData?.length === 0 && (
                                <ZfBox
                                  style={{
                                    padding: 'var(--gap-vertical-xs)',
                                    paddingBottom: 'var(--gap-vertical-xs)',
                                    paddingLeft: 'var(--gap-horizontal-md)',
                                  }}
                                >
                                  <ZfTypography variant="body-small" color="normal">
                                    {t('pb.cashaccount.empty.description')}
                                  </ZfTypography>
                                </ZfBox>
                              )}
                              {cashAccountFilteredData && (
                                <List
                                  rowCount={cashAccountFilteredData.length}
                                  rowHeight={DROPDOWN_HEIGHT}
                                  overscanCount={5}
                                  style={{
                                    height: Math.min(cashAccountFilteredData.length, 10) * DROPDOWN_HEIGHT,
                                    width: '100%',
                                  }}
                                  rowProps={{
                                    item: item,
                                  }}
                                  rowComponent={Row}
                                ></List>
                              )}
                            </ZfInput>
                          </ZfFormField>
                          <ZfIconButton
                            size="large"
                            hierarchy="tertiary"
                            onClick={() => {
                              const newList = values.cashAccounts.filter((_, i) => i !== index);
                              setFieldValue('cashAccounts', newList);
                            }}
                            disabled={values.cashAccounts.length === 1}
                          >
                            <ZfIcon name="delete" />
                          </ZfIconButton>
                        </ZfStack>
                      );
                    })}
                  </ZfStack>
                  <ZfTextButton
                    style={{ maxWidth: '200px' }}
                    onClick={() => {
                      setFieldValue('cashAccounts', [...values.cashAccounts, { currency: '', accountType: '' }]);
                    }}
                  >
                    {t('pb.cashaccount.addbutton')}
                  </ZfTextButton>
                </ZfStack>
              </ZfDialogContent>
              <ZfDialogFooter>
                <ZfStack slot="primary" direction="row" spacing="small">
                  <ZfTextButton disabled={isSubmitting} size="medium" hierarchy="tertiary" onClick={handleClose}>
                    {t('pb.investmentproduct.cancel')}
                  </ZfTextButton>
                  <ZfTextButton disabled={isSubmitting} size="medium" onClick={submitForm}>
                    {t('pb.investmentproduct.save')}
                  </ZfTextButton>
                </ZfStack>
              </ZfDialogFooter>
            </ZfDialog>
            <BannerErrorDialog
              title={t('pb.portfolio.error.database.title')}
              showDialog={showErrorDialog}
              onDialogClose={() => setShowErrorDialog(false)}
              error={errorObject}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default AddCashAccountDialog;
